import { resolveCustomCountryName } from './customCountryNames';
import { ISOCountryCode } from './ISOCountryCode';

/**
 * Custom mappings for PM-specific or deprecated country codes
 */
const PMCountryCodes: Record<string, ISOCountryCode> = {
  // MPM uses the wrong code for Kosovo
  XZ: ISOCountryCode.Kosovo,

  // Reserved for United Kingdom. The official ISO code is GB
  UK: ISOCountryCode.UnitedKingdom,

  // Deprecated code after name changed from Burma to Myanmar
  BU: ISOCountryCode.Myanmar,
};

// Country code values to avoid repeated Object.values() calls
export const countryCodesList = Object.values(ISOCountryCode);

/**
 * Parse a string value into a valid ISOCountryCode
 * Supports standard ISO codes and PM-specific mappings
 *
 * @param value - The country code string to parse (case-insensitive)
 * @returns The validated ISOCountryCode
 * @throws {Error} If the country code is invalid
 *
 * @example
 * ```ts
 * parseCountryCode('US') // ISOCountryCode.UnitedStatesAmerica
 * parseCountryCode('uk') // ISOCountryCode.UnitedKingdom (PM mapping)
 * parseCountryCode('XZ') // ISOCountryCode.Kosovo (PM mapping)
 * ```
 */
export function parseCountryCode(value: string): ISOCountryCode {
  const upperValue = value.toUpperCase();

  // Check PM-specific codes first (faster than array search)
  const pmCountryCode = PMCountryCodes[upperValue];
  if (pmCountryCode) {
    return pmCountryCode;
  }

  // Check standard ISO codes
  const countryCode = countryCodesList.find((code) => code === upperValue);

  if (!countryCode) {
    throw new Error(`Invalid ISO country code: "${value}"`);
  }

  return countryCode as ISOCountryCode;
}

/**
 * Type guard to check if a string is a valid ISOCountryCode
 *
 * @param value - The value to check
 * @returns True if the value is a valid country code
 *
 * @example
 * ```ts
 * if (isISOCountryCode('US')) {
 *   // TypeScript knows value is ISOCountryCode here
 * }
 * ```
 */
export function isISOCountryCode(value: string): value is ISOCountryCode {
  const upperValue = value.toUpperCase();
  return (
    upperValue in PMCountryCodes ||
    countryCodesList.includes(upperValue as ISOCountryCode)
  );
}

/**
 * Get the localized country name for a given country code
 * Uses the browser's Intl.DisplayNames API
 *
 * @param value - The country code (case-insensitive)
 * @param language - The language code (e.g., 'en', 'de', 'es')
 * @returns The localized country name, or the uppercase code if not found
 *
 * @example
 * ```ts
 * localizedCountryName('US', 'en') // "United States"
 * localizedCountryName('DE', 'es') // "Alemania"
 * ```
 */
export function localizedCountryName(value: string, language: string): string {
  try {
    const countryCode = parseCountryCode(value);
    const customName = resolveCustomCountryName(countryCode, language);
    if (customName) {
      return customName;
    }

    const regionNames = new Intl.DisplayNames([language], {
      type: 'region',
    });
    return regionNames.of(countryCode) ?? countryCode;
  } catch {
    return value.toUpperCase();
  }
}

/**
 * Get the country code enum key by its ISO code value
 * Useful for reverse lookups
 *
 * @param code - The ISO country code value (e.g., 'US', 'GB')
 * @returns The enum key name (e.g., 'UnitedStatesAmerica', 'UnitedKingdom') or undefined
 *
 * @example
 * ```ts
 * getCountryCodeByName('US') // 'UnitedStatesAmerica'
 * getCountryCodeByName('GB') // 'UnitedKingdom'
 * ```
 */
export function getCountryCodeByName(
  code: string,
): keyof typeof ISOCountryCode | undefined {
  const upperCode = code.toUpperCase();
  const entry = Object.entries(ISOCountryCode).find(
    ([, value]) => value === upperCode,
  );
  return entry ? (entry[0] as keyof typeof ISOCountryCode) : undefined;
}
